'use client';

import { Heart, MessageCircle, Plus, Bookmark, Share2, Clock, Play, Check, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatCopyCount, ABILITY_TYPE_CONFIG } from '@/lib/community/types';
import type { CommunityPost } from '@/lib/community/types';
import { relativeTime } from '@/lib/community/utils';
import { useAuthContext } from '@/lib/auth-context';
import { AbilityTypeBadge } from './ability-type-badge';
import { CreatorLevelBadge } from './creator-level-badge';
import { PostMarkdown } from './post-markdown';
import { CommentsSection } from './comments-section';
import { CopyToAccountPanel } from './copy-to-account-panel';
import { DeployTeamPanel } from './deploy-team-panel';
import { CoverImageAdaptive } from './cover-image-adaptive';
import { PostShowcase } from './showcase/post-showcase';
import { CasePreviewModal } from './case-preview-modal';
import { ShareLinkPopup } from './share-link-popup';
import { PaymentConfirmPanel } from './payment-confirm-panel';
import type { PostDetailHandlers, PanelStates } from '@/lib/hooks/use-post-detail';

interface PostDetailContentProps {
	post: CommunityPost | null;
	loading: boolean;
	handlers: PostDetailHandlers;
	panelStates: PanelStates;
	className?: string;
	sectionWrapper?: (index: number, children: React.ReactNode) => React.ReactNode;
}

/**
 * PostDetailContent — shared content body used by all three post-detail variants
 * (page / modal / inline). Full-width left-aligned layout matching old/detail style.
 */
export function PostDetailContent({ post, loading, handlers, panelStates, className, sectionWrapper = (_i, children) => children }: PostDetailContentProps) {
	const { handleLike, handleFavorite, handleCopyConfirm, handleDeployConfirm, handleEdit } = handlers;

	const {
		copyPanelOpen,
		setCopyPanelOpen,
		deployPanelOpen,
		setDeployPanelOpen,
		casePreviewOpen,
		setCasePreviewOpen,
		paymentPanelOpen,
		setPaymentPanelOpen,
		shareLinkOpen,
		setShareLinkOpen,
	} = panelStates;

	const { currentUser, isLoggedIn, openLogin } = useAuthContext();
	const navigate = useNavigate();

	if (loading || !post) {
		return (
			<div className="flex items-center justify-center py-20">
				<div className="h-6 w-6 border-2 border-muted-foreground/30 border-t-foreground rounded-full animate-spin" />
			</div>
		);
	}

	const isOwner = !!currentUser && currentUser.userId === post.author.id;

	return (
		<div className={className}>
			{/* Section 0 — Cover image */}
			{sectionWrapper(
				0,
				post.coverImage && (
					<div className="w-full bg-secondary/30">
						<CoverImageAdaptive alt={post.title} src={post.coverImage} />
					</div>
				),
			)}

			<div className="px-6 py-5">
				{/* Section 1 — Meta line + Title + Author */}
				{sectionWrapper(
					1,
					<>
						{/* Meta line */}
						<div className="flex items-center gap-2 mb-3">
							<AbilityTypeBadge size="sm" type={post.abilityType} />
							<span className="text-xs text-muted-foreground flex items-center gap-1">
								<Clock className="h-3 w-3" />
								{relativeTime(post.createdAt)}
							</span>
							{isOwner && (
								<button
									className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
									onClick={handleEdit}
									title="编辑帖子"
									type="button"
								>
									<Pencil className="h-3.5 w-3.5" />
									编辑
								</button>
							)}
						</div>

						{/* Title */}
						<h2 className="text-xl font-semibold text-foreground mb-4 leading-snug">{post.title}</h2>

						{/* Author */}
						<div className="flex items-center gap-3 mb-6">
							<img alt={post.author.displayName} className="h-10 w-10 rounded-full object-cover bg-muted" src={post.author.avatar} />
							<div>
								<div className="flex items-center gap-1.5">
									<span className="text-sm font-medium text-foreground">{post.author.displayName}</span>
									{post.author.isVerified && <span className="text-[10px] text-emerald-500">✓</span>}
									<CreatorLevelBadge level={post.author.creatorLevel} />
								</div>
								<p className="text-xs text-muted-foreground line-clamp-1">{post.author.bio}</p>
							</div>
						</div>
					</>,
				)}

				{/* Section 3 — Description */}
				{sectionWrapper(
					3,
					<div className="mb-6">
						<PostMarkdown content={post.description} />
					</div>,
				)}

				{/* Section 4 — PostShowcase */}
				{sectionWrapper(4, <PostShowcase post={post} />)}

				{/* Section 5 — Tags */}
				{sectionWrapper(
					5,
					post.tags.length > 0 && (
						<div className="mb-5">
							<div className="flex flex-wrap gap-2">
								{post.tags.map((tag) => (
									<span className="text-xs bg-secondary/70 text-foreground/80 px-2.5 py-1 rounded-full" key={tag}>
										#{tag}
									</span>
								))}
							</div>
						</div>
					),
				)}

				{/* Section 6 — Copy count */}
				{sectionWrapper(6, <div className="mb-6 text-xs text-muted-foreground">{formatCopyCount(post.stats.copies)}</div>)}

				{/* Section 7 — Action bar */}
				{sectionWrapper(
					7,
					<div className="flex items-center justify-between py-4 border-y border-border/30 mb-6">
						<div className="flex items-center gap-1">
							<button
								className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors ${post.isLiked ? 'text-red-500 bg-red-500/10' : 'text-muted-foreground hover:text-foreground hover:bg-foreground/5'
									}`}
								onClick={() => {
									if (!isLoggedIn) {
										openLogin();
										return;
									}
									handleLike();
								}}
								type="button"
							>
								<Heart className={`h-3.5 w-3.5 ${post.isLiked ? 'fill-current' : ''}`} />
								{post.stats.likes}
							</button>
							<button
								className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors ${post.isFavorited ? 'text-amber-500 bg-amber-500/10' : 'text-muted-foreground hover:text-foreground hover:bg-foreground/5'
									}`}
								onClick={() => {
									if (!isLoggedIn) {
										openLogin();
										return;
									}
									handleFavorite();
								}}
								type="button"
							>
								<Bookmark className={`h-3.5 w-3.5 ${post.isFavorited ? 'fill-current' : ''}`} />
								收藏
							</button>
							<button
								className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors"
								onClick={() => setShareLinkOpen(true)}
								type="button"
							>
								<Share2 className="h-3.5 w-3.5" />
								分享
							</button>
							<span className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground">
								<MessageCircle className="h-3.5 w-3.5" />
								{post.stats.comments}
							</span>
						</div>

						{/* Type-aware CTA buttons — only show for non-discussion posts with aiMessage */}
						{post.aiMessage && (
							<div className="flex items-center gap-2">
								<button
									className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium border transition-colors ${ABILITY_TYPE_CONFIG[post.abilityType].borderClass} ${ABILITY_TYPE_CONFIG[post.abilityType].outlineTextClass} ${ABILITY_TYPE_CONFIG[post.abilityType].hoverBgClass} hover:text-white`}
									onClick={() => setCasePreviewOpen(true)}
									type="button"
								>
									<Play className="h-3.5 w-3.5" />
									查看案例
								</button>

								{post.isJoined ? (
									<span
										className={`flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-medium cursor-default ${ABILITY_TYPE_CONFIG[post.abilityType].joinDoneBgClass}`}
									>
										<Check className="h-4 w-4" />
										{post.abilityType === 'agent-team' ? '已部署' : '已加入'}
									</span>
								) : (
									<button
										className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium text-white transition-colors ${ABILITY_TYPE_CONFIG[post.abilityType].joinBgClass}`}
										onClick={() => {
											if (post.price && post.abilityType !== 'agent-team') {
												setPaymentPanelOpen(true);
											} else if (post.abilityType === 'agent-team') {
												post.price ? setPaymentPanelOpen(true) : setDeployPanelOpen(true);
											} else {
												setCopyPanelOpen(true);
											}
										}}
										type="button"
									>
										<Plus className="h-4 w-4" />
										{ABILITY_TYPE_CONFIG[post.abilityType].joinLabel}
										{post.price && <span className="text-white/70">¥{post.price}</span>}
									</button>
								)}
							</div>
						)}
					</div>,
				)}

				{/* Section 9 — Comments */}
				{sectionWrapper(9, <CommentsSection postAuthorId={post.author.id} postId={post.id} />)}
			</div>

			{/* Panel overlays — rendered WITHOUT sectionWrapper */}
			<CopyToAccountPanel isOpen={copyPanelOpen} onClose={() => setCopyPanelOpen(false)} onConfirm={handleCopyConfirm} post={post} />

			{post.abilityType === 'agent-team' && (
				<DeployTeamPanel isOpen={deployPanelOpen} onClose={() => setDeployPanelOpen(false)} onConfirm={handleDeployConfirm} post={post} />
			)}

			{casePreviewOpen && <CasePreviewModal onClose={() => setCasePreviewOpen(false)} post={post} />}

			{post.price && <PaymentConfirmPanel isOpen={paymentPanelOpen} onClose={() => setPaymentPanelOpen(false)} onConfirm={handleCopyConfirm} post={post} />}

			{shareLinkOpen && <ShareLinkPopup onClose={() => setShareLinkOpen(false)} title={post.title} url={`${window.location.origin}/community/post/${post.id}`} />}
		</div>
	);
}
